import { ObjectSelectInput, ObjectSelectProps } from '@frontend/components/basic-forms';
import { Spinner } from '@frontend/elements';
import { CommonMessage, useLocale } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { defaultMapWithAttributes } from '@frontend/product/badge';
import { IProduct, ProductQueryParams } from '@frontend/product/types';
import { useAccountRepository } from '@frontend/repository';
import React, { useMemo, useState } from 'react';

interface KitProductSelectProps extends ObjectSelectProps<IProduct, ProductQueryParams> {
    excludeProductId?: string | string[];
}

const ID = 'kit-product-select';
export const KitProductSelect = (props: KitProductSelectProps) => {
    const { excludeProductId, ...selectProps } = props;
    const { locale } = useLocale();
    const { currentAccount } = useAccountRepository();
    const [v, cV] = useState<IProduct | null>(null);

    const excludedIds = useMemo(() => {
        if (!excludeProductId) return new Set<string>();
        return new Set(Array.isArray(excludeProductId) ? excludeProductId : [excludeProductId]);
    }, [excludeProductId]);

    if (!currentAccount) {
        return <Spinner />;
    }

    return (
        <ObjectSelectInput<IProduct, ProductQueryParams>
            {...selectProps}
            id={selectProps.id ? `${ID}-${selectProps.id}` : ID}
            label={selectProps.label || CommonMessage.OBJECTS.PRODUCT.KIT}
            onChange={(v) => {
                selectProps.onChange && selectProps.onChange(v?.id || null);
                selectProps.onChangeObject && selectProps.onChangeObject(v);
                cV(v);
            }}
            queryParams={selectProps.queryParams}
            value={v}
            idValue={selectProps.value || v?.id}
            fetch={(arg) =>
                ProductClient.fetchProductsNotInKit(currentAccount, { ...arg, sort_field: 'name', sort_direction: 'asc' }).then((res) => {
                    if (excludedIds.size === 0) return res;
                    return {
                        ...res,
                        results: res.results.filter((p) => !excludedIds.has(p.id))
                    };
                })
            }
            singleFetch={(id) => ProductClient.resolveProducts([id]).then((res) => res.results[0])}
            itemLabel={(prod: IProduct) => defaultMapWithAttributes(prod, locale, ['ean', 'sku'])}
            useQueryParam={
                selectProps.allowURLChange
                    ? {
                          param: 'kit_id',
                          fetch: (id) =>
                              ProductClient.resolveProducts([id]).then((res) => {
                                  return res.results[0];
                              })
                      }
                    : undefined
            }
        />
    );
};
